import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { createLocalVideoTrack } from "livekit-client";
import { LocalUserChoices } from "@livekit/components-react";
import PreJoinControlBar from "./PreJoinControlBar";
import { VideoOffIcon } from "lucide-react";

interface PreJoinScreenProps {
  containerized?: boolean;
  onJoin: (choices: LocalUserChoices) => void;
}

const PreJoinScreen: React.FC<PreJoinScreenProps> = ({
  containerized = false,
  onJoin,
}) => {
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("");
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>("");
  const [localVideoTrack, setLocalVideoTrack] =
    useState<MediaStreamTrack | null>(null);

  const handleVideoToggle = async () => {
    const newVideoEnabled = !videoEnabled;
    setVideoEnabled(newVideoEnabled);

    if (newVideoEnabled) {
      const videoTrack = await createLocalVideoTrack({
        deviceId: selectedVideoDevice,
      });
      setLocalVideoTrack(videoTrack.mediaStreamTrack);
    } else {
      if (localVideoTrack) {
        localVideoTrack.stop();
        setLocalVideoTrack(null);
      }
    }
  };

  const handleVideoDeviceChange = async (deviceId: string) => {
    setSelectedVideoDevice(deviceId);

    if (videoEnabled) {
      if (localVideoTrack) {
        localVideoTrack.stop();
      }
      const videoTrack = await createLocalVideoTrack({
        deviceId: deviceId,
      });
      setLocalVideoTrack(videoTrack.mediaStreamTrack);
    }
  };

  const handleJoin = () => {
    const choices: LocalUserChoices = {
      username: "User",
      audioEnabled,
      videoEnabled,
      audioDeviceId: selectedAudioDevice,
      videoDeviceId: selectedVideoDevice,
    };

    if (localVideoTrack) {
      localVideoTrack.stop();
    }

    onJoin(choices);
  };

  useEffect(() => {
    const enumerateDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(
        (device) => device.kind === "audioinput",
      );
      const videoInputs = devices.filter(
        (device) => device.kind === "videoinput",
      );

      setAudioDevices(audioInputs);
      setVideoDevices(videoInputs);

      if (audioInputs.length > 0) {
        setSelectedAudioDevice(audioInputs[0].deviceId);
      }
      if (videoInputs.length > 0) {
        setSelectedVideoDevice(videoInputs[0].deviceId);
      }

      if (videoInputs.length > 0) {
        const videoTrack = await createLocalVideoTrack({
          deviceId: videoInputs[0].deviceId,
        });
        setLocalVideoTrack(videoTrack.mediaStreamTrack);
      }
    };

    enumerateDevices();
    return () => {
      if (localVideoTrack) {
        localVideoTrack.stop();
      }
    };
  }, []);

  return (
    <Box
      sx={{
        ...(containerized
          ? {
              width: "100%",
              height: "100%",
              borderRadius: "12px",
              overflow: "hidden",
            }
          : {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1300,
            }),
        backgroundColor: "#232323",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "16px",
        gap: 3,
      }}
    >
      {/* Video Preview */}
      <Box
        sx={{
          width: "400px",
          height: "300px",
          backgroundColor: "#232323",
          borderRadius: "12px",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {videoEnabled && localVideoTrack ? (
          <video
            ref={(videoElement) => {
              if (videoElement && localVideoTrack) {
                videoElement.srcObject = new MediaStream([localVideoTrack]);
              }
            }}
            autoPlay
            muted
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Box sx={{ textAlign: "center", color: "white" }}>
            <VideoOffIcon size={64} />
            <Typography variant="body1" sx={{ mt: 1 }}>
              Camera is off
            </Typography>
          </Box>
        )}
      </Box>

      {/* Control Bar */}
      <PreJoinControlBar
        audioEnabled={audioEnabled}
        videoEnabled={videoEnabled}
        audioDevices={audioDevices}
        videoDevices={videoDevices}
        selectedAudioDevice={selectedAudioDevice}
        selectedVideoDevice={selectedVideoDevice}
        onAudioToggle={() => setAudioEnabled(!audioEnabled)}
        onVideoToggle={handleVideoToggle}
        onAudioDeviceChange={setSelectedAudioDevice}
        onVideoDeviceChange={handleVideoDeviceChange}
        onJoin={handleJoin}
      />
    </Box>
  );
};

export default PreJoinScreen;
